/**
 * Created: 2026-10-10
 * Initial commit: Extract useSlotActions to shared (feat/event/mr-54)
 *
 * Hook encapsulating derived action state and slot mutation handlers.
 * Used by both web admin and miniapp admin slot pages.
 */

import type { DocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { useMemo, useState } from 'react'

interface GraphqlSuccess {
  __typename: 'GraphqlSuccess'
  success: boolean
}

interface GraphqlRequestError {
  __typename: 'GraphqlRequestError'
  code: string
  message: string
}

type OverrideSlotsResult = GraphqlSuccess | GraphqlRequestError

interface OverrideSlotsMutationData {
  overrideSlots: OverrideSlotsResult
}

interface Court {
  id: string
  name: string
  displayOrder: number
  sport: string
}

interface SlotMeta {
  status: string
  startTime: string
  eventId?: string | null
  reason?: string | null
}

interface UseSlotActionsParams {
  selectedCells: { hour: number; courtId: string }[]
  setSelectedCells: React.Dispatch<
    React.SetStateAction<{ hour: number; courtId: string }[]>
  >
  slotLookup: Map<string, SlotMeta>
  courts: Court[]
  slotsRefetch: () => void
  onError: (message: string) => void
  SlotStatus: {
    Open: string
    Reserved: string
  }
  OverrideSlotsDocument: DocumentNode
}

export function useSlotActions({
  selectedCells,
  setSelectedCells,
  slotLookup,
  courts,
  slotsRefetch,
  onError,
  SlotStatus,
  OverrideSlotsDocument,
}: UseSlotActionsParams) {
  const [overrideSlots, { loading: mutating }] =
    useMutation<OverrideSlotsMutationData>(OverrideSlotsDocument)

  const [reasonDialogOpen, setReasonDialogOpen] = useState(false)
  const [reasonInput, setReasonInput] = useState('')
  const [priceDialogOpen, setPriceDialogOpen] = useState(false)
  const [priceInput, setPriceInput] = useState('')

  const { hasOpen, hasReserved, canCreateEvent, eventSlots } = useMemo(() => {
    let open = false
    let reserved = false
    for (const cell of selectedCells) {
      const meta = slotLookup.get(`${cell.hour}-${cell.courtId}`)
      if (!meta) continue
      if (meta.status === SlotStatus.Open) open = true
      if (
        meta.status === SlotStatus.Reserved &&
        !meta.eventId &&
        meta.reason?.split('\n')[0] !== '韵动吧'
      )
        reserved = true
    }

    const allOpen =
      selectedCells.length > 0 &&
      selectedCells.every(
        (c) => slotLookup.get(`${c.hour}-${c.courtId}`)?.status === SlotStatus.Open,
      )

    const courtHoursMap = new Map<string, number[]>()
    const hourCourtsMap = new Map<number, string[]>()
    for (const cell of selectedCells) {
      if (!courtHoursMap.has(cell.courtId)) courtHoursMap.set(cell.courtId, [])
      courtHoursMap.get(cell.courtId)?.push(cell.hour)
      if (!hourCourtsMap.has(cell.hour)) hourCourtsMap.set(cell.hour, [])
      hourCourtsMap.get(cell.hour)?.push(cell.courtId)
    }
    const courtIdList = [...courtHoursMap.keys()]

    const allHours = [...new Set(selectedCells.map((c) => c.hour))].sort(
      (a, b) => a - b,
    )
    // Each hour must have the same number of courts selected
    const firstCount =
      allHours.length > 0 ? (hourCourtsMap.get(allHours[0])?.length ?? 0) : 0
    const sameCourtCount =
      allHours.length > 0 &&
      allHours.every((h) => (hourCourtsMap.get(h)?.length ?? 0) === firstCount)
    const consecutive =
      allHours.length > 0 &&
      allHours.every((h, i) => i === 0 || h === allHours[i - 1] + 1)

    const courtCategoryMap = new Map(courts.map((c) => [c.id, c.sport]))
    const categories = new Set(courtIdList.map((id) => courtCategoryMap.get(id)))
    const sameCategory = categories.size === 1

    const courtNameMap = new Map(courts.map((c) => [c.id, c.name]))
    // The backend rejects events starting less than one hour from now
    const minStartTime = Date.now() + 60 * 60 * 1000
    const startsLater = selectedCells.every((c) => {
      const startTime = slotLookup.get(`${c.hour}-${c.courtId}`)?.startTime
      return startTime !== undefined && new Date(startTime).getTime() >= minStartTime
    })
    const canCreate =
      allOpen && sameCourtCount && consecutive && sameCategory && startsLater
    const slots = canCreate
      ? selectedCells.map((c) => ({
          courtId: c.courtId,
          courtName: courtNameMap.get(c.courtId) ?? '',
          startTime: slotLookup.get(`${c.hour}-${c.courtId}`)?.startTime ?? '',
        }))
      : []

    return {
      hasOpen: open,
      hasReserved: reserved,
      canCreateEvent: canCreate,
      eventSlots: slots,
    }
  }, [selectedCells, slotLookup, courts, SlotStatus.Open, SlotStatus.Reserved])

  const handleReserveOpen = () => {
    setReasonInput('')
    setReasonDialogOpen(true)
  }

  const handleReserveConfirm = async () => {
    const inputs: Array<{
      courtId: string
      startTime: string
      status: string
      reason?: string
    }> = []
    for (const cell of selectedCells) {
      const meta = slotLookup.get(`${cell.hour}-${cell.courtId}`)
      if (meta?.status !== SlotStatus.Open) continue
      inputs.push({
        courtId: cell.courtId,
        startTime: meta.startTime,
        status: SlotStatus.Reserved,
        reason: reasonInput || undefined,
      })
    }
    if (inputs.length === 0) return
    setReasonDialogOpen(false)
    const result = await overrideSlots({ variables: { slots: inputs } })
    if (result.data?.overrideSlots.__typename === 'GraphqlRequestError') {
      onError(result.data.overrideSlots.message ?? '预留失败')
    }
    setSelectedCells([])
    await slotsRefetch()
  }

  const handleRelease = async () => {
    const inputs: Array<{
      courtId: string
      startTime: string
      status: string
    }> = []
    for (const cell of selectedCells) {
      const meta = slotLookup.get(`${cell.hour}-${cell.courtId}`)
      if (
        meta?.status !== SlotStatus.Reserved ||
        meta.eventId ||
        meta.reason?.split('\n')[0] === '韵动吧'
      )
        continue
      inputs.push({
        courtId: cell.courtId,
        startTime: meta.startTime,
        status: SlotStatus.Open,
      })
    }
    if (inputs.length === 0) return
    const result = await overrideSlots({ variables: { slots: inputs } })
    if (result.data?.overrideSlots.__typename === 'GraphqlRequestError') {
      onError(result.data.overrideSlots.message ?? '释放失败')
    }
    setSelectedCells([])
    slotsRefetch()
  }

  const handleChangePriceOpen = () => {
    setPriceInput('')
    setPriceDialogOpen(true)
  }

  const handleChangePriceConfirm = async () => {
    if (priceInput === '' || Number(priceInput) < 0 || Number(priceInput) > 999) return
    const price = priceInput
    const inputs: Array<{
      courtId: string
      startTime: string
      price: string
    }> = []
    for (const cell of selectedCells) {
      const meta = slotLookup.get(`${cell.hour}-${cell.courtId}`)
      // The backend reprices only free slots
      if (meta?.status !== SlotStatus.Open) continue
      inputs.push({ courtId: cell.courtId, startTime: meta.startTime, price })
    }
    if (inputs.length === 0) return
    setPriceDialogOpen(false)
    const result = await overrideSlots({ variables: { slots: inputs } })
    if (result.data?.overrideSlots.__typename === 'GraphqlRequestError') {
      onError(result.data.overrideSlots.message ?? '改价失败')
    }
    setSelectedCells([])
    slotsRefetch()
  }

  return {
    hasOpen,
    hasReserved,
    canCreateEvent,
    eventSlots,
    mutating,
    reasonDialogOpen,
    setReasonDialogOpen,
    reasonInput,
    setReasonInput,
    priceDialogOpen,
    setPriceDialogOpen,
    priceInput,
    setPriceInput,
    handleReserveOpen,
    handleReserveConfirm,
    handleRelease,
    handleChangePriceOpen,
    handleChangePriceConfirm,
  }
}
